import { RootState } from "common/reducers";
import { createSelector } from "reselect";
import { findProductBracket, IBracketOpening, ProductRecord } from "common/records/Product";
import { ProductOptionRecord } from "common/records/ProductOption";
import { Nullable } from "common/types";
import { imageOverview } from "common/selectors/image.selectors";
import { getUrl, IFileRecord } from "common/records/Image";
import { List, Map } from "immutable";
import { DocumentRecord } from "common/records/Document";
import { document } from "common/selectors/document.selectors";

export const model = "products";

/**
 * Loads the byId map
 *
 * @param {RootState} state The RootState
 * @returns {Map< number, ProductRecord>} The byId map
 */
export const byId = (state: RootState) => state.getIn([model, "byId"]);

export const productById = (state: RootState, id: number): Nullable<ProductRecord> => byId(state).get(id);

/**
 * Loads the productOverviewsById map
 *
 * @param {RootState} state The RootState
 * @returns {Map< number, ProductRecord>} The byId map
 */
export const productOverviewsById = (state: RootState) => state.getIn([model, "productOverviewsById"]);

export const productOverviewById = (state: RootState, id: number): Nullable<ProductRecord> =>
  productOverviewsById(state).get(id);

/**
 * The ROOT products of a hydrated snapshot: resolves the APL record's `product_ids` (roots only)
 * against the global `productOverviewsById` map the snapshot was fanned into. Descendants resolve
 * via `productChildren` / `productOverviewById`. Empty List when unhydrated.
 *
 * @param {RootState} state The RootState
 * @param {number | null | undefined} activatedPriceListId
 * @returns {List<ProductRecord>} the snapshot's root products, in stored-id order
 */
const EMPTY_ROOT_PRODUCTS = List<ProductRecord>();

export const productsForActivatedPriceList = createSelector(
  [
    (state: RootState, activatedPriceListId: Nullable<number>) =>
      activatedPriceListId
        ? (state.getIn(["activatedPriceLists", "byId", activatedPriceListId]) as
            | { product_ids: List<number> }
            | undefined)
        : undefined,
    (state: RootState) => state.getIn(["products", "productOverviewsById"]) as Map<number, ProductRecord>,
  ],
  (apl, productOverviewsById): List<ProductRecord> => {
    if (!apl || !apl.product_ids) return EMPTY_ROOT_PRODUCTS;
    return apl.product_ids
      .map((id: number) => productOverviewsById?.get(id) as Nullable<ProductRecord>)
      .filter((p: Nullable<ProductRecord>): p is ProductRecord => !!p) as List<ProductRecord>;
  },
);

// Stable empty-map reference for the unhydrated path. Returning a fresh `Map()` each call would
// change the input selector's identity every render and recompute the `createSelector`s that take
// it as an input (e.g. `productLeavesForActivatedPriceList`) throughout the loading window.
const EMPTY_PRODUCTS_MAP = Map<number, ProductRecord>();

/**
 * Reads the global `productOverviewsById` map (the snapshot's products are fanned into it by
 * `RECEIVE_ACTIVATED_PRICE_LIST`). Internal input selector — returns a stable Immutable Map
 * reference straight out of state, falling back to a shared empty map when unhydrated.
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, ProductRecord>} the product overview map, empty when not hydrated
 */
const overviewsByIdInput = (state: RootState): Map<number, ProductRecord> =>
  (state.getIn(["products", "productOverviewsById"]) as Map<number, ProductRecord>) ?? EMPTY_PRODUCTS_MAP;

/**
 * Direct children of a product, sorted by sort_order: resolves `children_ids` against the global
 * `productOverviewsById` map, dropping unresolved ids. `leafNodesOnly` keeps only leaf children —
 * flagged `leaf` or with no `children_ids` — matching rsf-app's `includeParent: false`.
 *
 * @param {RootState} state The RootState
 * @param {{ productId: Nullable<number>, leafNodesOnly?: boolean }} props
 * @returns {List<ProductRecord>} the resolved children, sorted by sort_order
 */
export const productChildren = createSelector(
  [
    overviewsByIdInput,
    // Key on the primitive props, not the whole props object — a fresh object literal each render
    // would be a new cache key on every call if used as an input.
    (_state: RootState, props: { productId: Nullable<number>; leafNodesOnly?: boolean }) => props.productId,
    (_state: RootState, props: { productId: Nullable<number>; leafNodesOnly?: boolean }) =>
      props.leafNodesOnly ?? false,
  ],
  (
    productsById: Map<number, ProductRecord>,
    productId: Nullable<number>,
    leafNodesOnly: boolean,
  ): List<ProductRecord> => {
    const parent = productId ? productsById.get(productId) : undefined;
    if (!parent) return List<ProductRecord>();
    return parent.children_ids
      .map((id: number) => productsById.get(id) as Nullable<ProductRecord>)
      .filter((p: Nullable<ProductRecord>): p is ProductRecord => !!p)
      .filter((p: ProductRecord) => !leafNodesOnly || p.leaf || p.children_ids.size === 0)
      .sortBy((p: ProductRecord) => p.sort_order ?? 0) as List<ProductRecord>;
  },
);

/**
 * The leaf products of a hydrated snapshot — the Product Selector's search corpus, scoped to the
 * estimate's snapshot by traversing the APL's root `product_ids` through `children_ids`.
 * Cycle-guarded; empty List when unhydrated.
 *
 * @param {RootState} state The RootState
 * @param {number | null | undefined} activatedPriceListId
 * @returns {List<ProductRecord>} the snapshot's leaf products
 */
const EMPTY_LEAF_PRODUCTS = List<ProductRecord>();

export const productLeavesForActivatedPriceList = createSelector(
  [
    (state: RootState, activatedPriceListId: Nullable<number>) =>
      activatedPriceListId
        ? (state.getIn(["activatedPriceLists", "byId", activatedPriceListId]) as
            | { product_ids: List<number> }
            | undefined)
        : undefined,
    overviewsByIdInput,
  ],
  (apl, productsById): List<ProductRecord> => {
    if (!apl || !apl.product_ids) return EMPTY_LEAF_PRODUCTS;
    const leaves: ProductRecord[] = [];
    const seen = new Set<number>();
    const stack: number[] = apl.product_ids.toArray();
    while (stack.length > 0) {
      const id = stack.pop() as number;
      if (seen.has(id)) continue;
      seen.add(id);
      const product = productsById.get(id);
      if (!product) continue;
      if (product.leaf || product.children_ids.size === 0) {
        leaves.push(product);
      } else {
        product.children_ids.forEach((childId: number) => {
          if (!seen.has(childId)) stack.push(childId);
        });
      }
    }
    return List(leaves);
  },
);

// Stable empty uuid→product map for the unhydrated path (same memoization rationale as
// EMPTY_PRODUCTS_MAP — keep the input selector's identity stable across renders).
const EMPTY_PRODUCTS_BY_UUID = Map<string, ProductRecord>();

/**
 * uuid→ProductRecord map for a hydrated snapshot, built over the snapshot's leaf products (the
 * sellable set, scoped to the APL by `productLeavesForActivatedPriceList`). The UUID is the stable
 * key across price-list versions (product ids are per-version), which is why template application
 * resolves by uuid. Mirrors rsf-app `cachedProductByUuid`. Empty map when unhydrated.
 *
 * @param {RootState} state The RootState
 * @param {number | null | undefined} activatedPriceListId
 * @returns {Map<string, ProductRecord>} the uuid→product map for the snapshot
 */
export const productsByUuidForActivatedPriceList = createSelector(
  [productLeavesForActivatedPriceList],
  (leaves: List<ProductRecord>): Map<string, ProductRecord> => {
    if (leaves.size === 0) return EMPTY_PRODUCTS_BY_UUID;
    let map = Map<string, ProductRecord>();
    leaves.forEach((product) => {
      if (product.uuid) map = map.set(product.uuid, product);
    });
    return map;
  },
);

/**
 * Resolves a single product by uuid within the estimate's hydrated snapshot, or null. Keyed on the
 * ESTIMATE's `activated_price_list_id` (pass `currentEstimateActivatedPriceListId`), NOT the org's
 * current APL. Mirrors rsf-app `cachedProductByUuid`.
 *
 * @param {RootState} state The RootState
 * @param {{ productUuid: string; activatedPriceListId: Nullable<number> }} props
 * @returns {Nullable<ProductRecord>} the resolved product, or null when not in the snapshot
 */
export const productByUuid = (
  state: RootState,
  props: { productUuid: string; activatedPriceListId: Nullable<number> },
): Nullable<ProductRecord> => {
  const { productUuid, activatedPriceListId } = props;
  if (!productUuid || !activatedPriceListId) return null;
  return productsByUuidForActivatedPriceList(state, activatedPriceListId).get(productUuid) ?? null;
};

/**
 * Resolves a List of product options by mapping the given ids against the global
 * `productOptions.byId` store, dropping any that don't resolve.
 *
 * @param {RootState} state The RootState
 * @param {{ productOptionIds: Nullable<List<number>> }} props
 * @returns {List<ProductOptionRecord>} the resolved options
 */
export const productOptionsByIds = (
  state: RootState,
  props: { productOptionIds: Nullable<List<number>> },
): List<ProductOptionRecord> => {
  const { productOptionIds } = props;
  if (!productOptionIds) return List<ProductOptionRecord>();
  const byId = state.getIn(["productOptions", "byId"]) as Map<number, ProductOptionRecord> | undefined;
  if (!byId) return List<ProductOptionRecord>();
  return productOptionIds
    .map((id: number) => byId.get(id) as Nullable<ProductOptionRecord>)
    .filter((o: Nullable<ProductOptionRecord>): o is ProductOptionRecord => !!o) as List<ProductOptionRecord>;
};

/**
 * Loads the provided product logo image record
 * Finds the first image of the product
 *
 * @param {RootState} state The RootState
 * @param {ProductRecord} product
 * @returns {ImageRecord} ImageRecord
 */
export const productLogo = (state: RootState, product: Nullable<ProductRecord>) => {
  if (!product) {
    return null;
  }

  const imageId = product.image_ids.first();
  if (!imageId) {
    return null;
  }
  return imageOverview(state, { imageId });
};

/**
 * A product's logo URL at the requested size, or null. Composes productOverviewById → productLogo
 * → getUrl, mirroring the inline chains the Product Selector rows and EstimateLineItem fall back to.
 *
 * @param {RootState} state The RootState
 * @param {{ productId: Nullable<number>, size: keyof IFileRecord }} props
 * @returns {string | null} the image URL, or null when absent
 */
export const productImageUrl = (
  state: RootState,
  props: { productId: Nullable<number>; size: keyof IFileRecord },
): string | null => {
  const { productId, size } = props;
  if (!productId) return null;
  const product = productOverviewById(state, productId);
  const image = productLogo(state, product);
  // getUrl returns "" when the record exists but lacks the requested size; coerce to null so the
  // return honors the `string | null` contract (callers treat null as "no image at this size").
  return (image ? getUrl(image, size) : null) || null;
};

/**
 * Walks the global overview store up via `parent_id`, returning the product's ancestor ids from
 * root down to (not including) the product itself. Empty for a root-level or absent product. Drives
 * the Product Selector's breadcrumb seed.
 *
 * @param {RootState} state The RootState
 * @param {{ productId: Nullable<number> }} props
 * @returns {List<number>} the ancestor ids, root-first, excluding the product itself
 */
export const productAncestorIds = createSelector(
  [overviewsByIdInput, (_state: RootState, props: { productId: Nullable<number> }) => props.productId],
  (productsById: Map<number, ProductRecord>, productId: Nullable<number>): List<number> => {
    const selected = productId ? productsById.get(productId) : undefined;
    if (!selected || selected.parent_id == null) return List<number>();
    let stack = List<number>();
    let parentId: number | null | undefined = selected.parent_id;
    // Guard against cycles with a generous cap on hierarchy depth.
    let guard = 0;
    while (parentId != null && guard < 50) {
      stack = stack.unshift(parentId);
      const parent = productsById.get(parentId);
      parentId = parent?.parent_id ?? null;
      guard += 1;
    }
    return stack;
  },
);

const EMPTY_PRODUCT_BY_ID_MAP = Map<number, ProductRecord>();

/**
 * All products (root + all descendants) reachable from a given APL, keyed by product ID.
 * Used for APL-scoped UUID lookups in validation — avoids cross-APL UUID contamination
 * when multiple APLs have been loaded into the shared productOverviewsById map.
 */
export const productsByIdForActivatedPriceList = createSelector(
  [
    (state: RootState, activatedPriceListId: Nullable<number>) =>
      activatedPriceListId
        ? (state.getIn(["activatedPriceLists", "byId", activatedPriceListId]) as
            | { product_ids: List<number> }
            | undefined)
        : undefined,
    overviewsByIdInput,
  ],
  (apl, productsById): Map<number, ProductRecord> => {
    if (!apl || !apl.product_ids) return EMPTY_PRODUCT_BY_ID_MAP;
    const entries: Array<[number, ProductRecord]> = [];
    const seen = new Set<number>();
    const stack: number[] = apl.product_ids.toArray();
    while (stack.length > 0) {
      const id = stack.pop() as number;
      if (seen.has(id)) continue;
      seen.add(id);
      const product = productsById.get(id);
      if (!product) continue;
      entries.push([id, product]);
      product.children_ids.forEach((childId: number) => {
        if (!seen.has(childId)) stack.push(childId);
      });
    }
    return Map(entries);
  },
);

const EMPTY_ANCESTORS = List<ProductRecord>();

export const ancestorsForActivatedPriceList = createSelector(
  [
    (state: RootState, props: { productId: Nullable<number>; activatedPriceListId: Nullable<number> }) =>
      productsByIdForActivatedPriceList(state, props.activatedPriceListId),
    (_state: RootState, props: { productId: Nullable<number> }) => props.productId,
  ],
  (productsById, productId): List<ProductRecord> => {
    if (!productId) {
      return EMPTY_ANCESTORS;
    }

    const chain: ProductRecord[] = [];
    const seen = new Set<number>();
    let current = productsById.get(productId);

    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      chain.unshift(current);
      current = current.parent_id ? productsById.get(current.parent_id) : undefined;
    }

    return chain.length ? List(chain) : EMPTY_ANCESTORS;
  },
);

export const productImageUrlForActivatedPriceList = (
  state: RootState,
  props: { productId: Nullable<number>; activatedPriceListId: Nullable<number>; size: keyof IFileRecord },
): string | null => {
  const { productId, activatedPriceListId, size } = props;
  if (!productId) return null;

  const product = productsByIdForActivatedPriceList(state, activatedPriceListId).get(productId);
  const image = productLogo(state, product);
  return (image ? getUrl(image, size) : null) || null;
};

const EMPTY_PARENT_PRODUCTS = List<ProductRecord>();

/**
 * The window/door parent products (with children) in an APL snapshot whose child brackets match
 * ALL of the given openings — the candidates for the wizard's Product Type switch. Scoped strictly
 * to the APL via `productsByIdForActivatedPriceList` (no global fallback). Ports rsf-app
 * cachedOpeningParentProducts. Callers must pass a reference-stable `openings` List for
 * memoization to hold.
 *
 * @param {RootState} state The RootState
 * @param {{ activatedPriceListId: Nullable<number>; openings: List<IBracketOpening> }} props
 * @returns {List<ProductRecord>} the matching parents, empty when no openings or unhydrated
 */
export const openingParentProducts = createSelector(
  [
    (state: RootState, props: { activatedPriceListId: Nullable<number> }) =>
      productsByIdForActivatedPriceList(state, props.activatedPriceListId ?? null),
    (_state: RootState, props: { openings: List<IBracketOpening> }) => props.openings,
  ],
  (aplProductsById, openings): List<ProductRecord> => {
    if (openings.size === 0) {
      return EMPTY_PARENT_PRODUCTS;
    }

    return aplProductsById
      .valueSeq()
      .filter((product) => (product.kind === "window" || product.kind === "door") && product.children_ids.size > 0)
      .filter((parent) => {
        const children = parent.children_ids
          .map((id) => aplProductsById.get(id))
          .filter((child): child is ProductRecord => Boolean(child));
        return openings.every((opening) => Boolean(findProductBracket(parent, children, opening)));
      })
      .toList();
  },
);

/**
 * Loads the ancestors of a product
 *
 * @param {RootState} state The RootState
 * @param {{ productId: number }} options
 * @returns {List<ProductRecord>} ProductRecord
 */
export const ancestors = createSelector(
  [productOverviewById, (state) => state, (_state: RootState, productId) => productId],
  (childProduct, state: RootState): List<ProductRecord> => {
    let products = List<ProductRecord>();
    if (!childProduct) {
      return products;
    }

    if (childProduct.parent_id) {
      products = products.concat(ancestors(state, childProduct.parent_id));
    }
    products = products.push(childProduct);

    return products;
  },
);

/**
 * Gets the parent.parent of a product, returns undefined if no parent.parent is found
 *
 * @param {RootState} state The RootState
 * @param {{ productId: number }}
 * @returns {ProductRecord} ProductRecord | undefined
 */
export const parentParent = createSelector(
  [productOverviewById, ancestors],
  (product, ancestors): Nullable<ProductRecord> => {
    if (!product) {
      return undefined;
    }

    return ancestors.reverse().get(2);
  },
);

/**
 * Loads the provided products document records
 *
 * @param {RootState} state The RootState
 * @param {{ productId: number }}
 * @returns {List<DocumentRecord>}
 */
export const productDocumentIds = createSelector(
  [productById, (state: RootState) => state, (_state: RootState, productId) => productId],
  (product, state: RootState): List<number> | null => {
    if (!product?.document_ids) {
      return null;
    }
    return product.document_ids;
  },
);

/**
 * Loads the provided products image records
 *
 * @param {RootState} state The RootState
 * @param {{ productId: number }}
 * @returns {List<number>}
 */
export const productImageIds = createSelector(
  [productById, (state: RootState) => state, (_state: RootState, productId) => productId],
  (product, state: RootState): List<number> | null => {
    if (!product?.image_ids) {
      return null;
    }
    return product.image_ids;
  },
);

/**
 * Loads the provided product documents list
 *
 * @param {RootState} state The RootState
 * @param {{ productId: number }}
 * @returns {List<DocumentRecord>}
 */
export const productDocumentOverviews = createSelector(
  [productOverviewById, (state: RootState) => state, (_state: RootState, productId) => productId],
  (product, state: RootState): List<DocumentRecord> | null => {
    let documents = List<DocumentRecord>();
    if (!product?.document_ids) {
      return documents;
    }
    product.document_ids.forEach((id) => {
      const productDocument = document(state, { documentId: id });
      if (productDocument) {
        documents = documents.push(productDocument);
      }
    });
    return documents;
  },
);
